'use client';

import { motion } from "framer-motion";
import { Metadata } from "next";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight, CheckCircle, BarChart3, Zap, Target, Users, LineChart, ShoppingCart, MessageSquare, Megaphone } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function MartechPage() {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const solutions = [
    {
      title: "AI-Powered Customer Segmentation",
      icon: Users,
      description: "Advanced machine learning algorithms analyze customer behavior patterns to create dynamic micro-segments for hyper-targeted marketing campaigns.",
      benefits: [
        "30-50% higher conversion rates compared to traditional segmentation",
        "Real-time segment adaptation based on behavior changes",
        "Predictive customer lifetime value modeling",
        "Multi-dimensional segmentation across dozens of variables"
      ]
    },
    {
      title: "Predictive Analytics Suite",
      icon: LineChart,
      description: "Forecast customer behavior, campaign performance, and market trends with our ensemble ML models trained on your business data and industry benchmarks.",
      benefits: [
        "85% average accuracy in conversion predictions", 
        "Automated anomaly detection in campaign performance",
        "Customer churn prediction with actionable prevention strategies",
        "Sales forecasting with market fluctuation adaptation"
      ]
    },
    {
      title: "AI Content Generation Platform",
      icon: MessageSquare,
      description: "Generate marketing copy, social media content, and product descriptions at scale while maintaining brand voice and optimizing for conversion.",
      benefits: [
        "700% increase in content production capacity",
        "Built-in SEO optimization for higher organic visibility", 
        "A/B testing automation to identify top-performing variants",
        "Adaptive tone and style matching to your brand guidelines"
      ]
    },
    {
      title: "Omnichannel Campaign Orchestration",
      icon: Megaphone,
      description: "Centralized AI platform that coordinates marketing activities across all channels with intelligent timing and personalized messaging.",
      benefits: [
        "40% average increase in campaign ROI", 
        "Real-time performance monitoring with automated adjustments",
        "Cross-channel attribution modeling",
        "Journey optimization based on individual user paths"
      ]
    },
    {
      title: "Dynamic Recommendation Engine",
      icon: ShoppingCart,
      description: "Personalized product and content recommendations that adapt in real-time to user behavior, contextual cues, and inventory status.",
      benefits: [
        "23% average increase in cart value", 
        "15-30% improvement in customer engagement metrics", 
        "Seamless integration with major e-commerce platforms",
        "Cold-start problem mitigation for new users"
      ]
    }
  ];

  const useCases = [
    {
      industry: "E-commerce",
      company: "GlobalShop",
      challenge: "Facing fierce competition and decreasing conversion rates despite increased ad spend.",
      solution: "Implemented our AI Customer Segmentation and Dynamic Recommendation Engine to personalize the shopping experience.",
      results: "45% increase in conversion rate, 27% higher average order value, and 3.2x improvement in email marketing performance within 90 days."
    },
    {
      industry: "Media & Entertainment",
      company: "StreamFlix",
      challenge: "Struggling with content promotion efficiency and subscription retention in a saturated market.",
      solution: "Deployed our Predictive Analytics Suite and AI Content Generation Platform to optimize content promotion strategy.",
      results: "31% reduction in customer churn, 52% increase in content engagement, and 4.7x ROI on marketing automation investment."
    },
    {
      industry: "Financial Services",
      company: "TrustBank",
      challenge: "Needed to modernize marketing approach while maintaining compliance with strict regulatory requirements.",
      solution: "Implemented our Omnichannel Campaign Orchestration with built-in compliance safeguards and content approval workflows.",
      results: "63% improvement in lead quality, 29% reduction in acquisition costs, and 100% regulatory compliance maintained across all campaigns."
    }
  ];

  const technologies = [
    {
      name: "Machine Learning",
      description: "Ensemble models including Random Forests, Gradient Boosting, and Deep Neural Networks for prediction and segmentation tasks."
    },
    {
      name: "Natural Language Processing",
      description: "Advanced language models fine-tuned on marketing content for generation, analysis, and optimization tasks."
    },
    {
      name: "Computer Vision",
      description: "Visual content analysis and generation capabilities for image-based marketing assets and analytics."
    },
    {
      name: "Real-time Analytics",
      description: "Stream processing architecture enabling instant insights and activation across marketing channels."
    },
    {
      name: "Privacy-preserving AI",
      description: "Federated learning and differential privacy techniques ensuring compliance with global data protection regulations."
    }
  ];

  return (
    <div className="container mx-auto max-w-7xl px-4 py-16 mt-16">
      {/* Hero Section */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        className="grid md:grid-cols-2 gap-12 items-center mb-20"
      >
        <div className="space-y-6">
          <Badge className="px-3 py-1 text-sm bg-orange-100 text-orange-700 hover:bg-orange-200">
            Marketing Technology
          </Badge>
          
          <h1 className="text-4xl font-bold tracking-tight md:text-5xl">
            AI-Powered MarTech Solutions
          </h1>
          
          <p className="text-xl text-muted-foreground">
            Transform your marketing operations with our cutting-edge artificial intelligence solutions designed to deliver personalized experiences, actionable insights, and measurable ROI.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <Button size="lg" variant="outline" asChild>
              <Link href="/services">
                Our Services
              </Link>
            </Button>
          </div>

          <div className="pt-4 flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <span className="text-sm">No-code implementation available</span>
          </div>
        </div>

        <div className="relative bg-gradient-to-br from-orange-100 via-rose-50 to-orange-100 rounded-2xl p-8 shadow-lg overflow-hidden">
          <div className="absolute inset-0 bg-white/40 rounded-2xl backdrop-blur-sm" />
          {/* Interactive Dashboard */}
          <div className="relative z-10 bg-white/95 backdrop-blur-md rounded-xl shadow-lg overflow-hidden">
            {/* Dashboard Header */}
            <div className="bg-gradient-to-r from-orange-600 to-pink-500 p-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-6 w-6 text-white" />
                  <h3 className="font-semibold text-white">Marketing Performance Dashboard</h3>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="text-white border-white/30 bg-white/10">
                    Live Data
                  </Badge>
                  <span className="text-white/80 text-sm flex items-center">
                    <Zap className="h-3.5 w-3.5 mr-1" />
                    Updated 5m ago
                  </span>
                </div>
              </div>
            </div>
            {/* Dashboard Tabs */}
            <div className="p-1 bg-gray-100/80">
              <Tabs defaultValue="overview" className="w-full">
                <TabsList className="grid grid-cols-3 h-8">
                  <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
                  <TabsTrigger value="campaigns" className="text-xs">Campaigns</TabsTrigger>
                  <TabsTrigger value="alerts" className="text-xs">Alerts</TabsTrigger>
                </TabsList>
                {/* Overview Tab */}
                <TabsContent value="overview" className="p-3">
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    {/* Segmentation Summary */}
                    <Card className="bg-white/70 shadow-sm">
                      <CardHeader className="pb-2 pt-3">
                        <CardTitle className="text-sm font-medium">Segmentation Summary</CardTitle>
                      </CardHeader>
                      <CardContent className="pb-3">
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="flex items-center">
                            <div className="h-2 w-2 rounded-full bg-orange-500 mr-1"></div>
                            <span>Active Segments: 18</span>
                          </div>
                          <div className="flex items-center">
                            <div className="h-2 w-2 rounded-full bg-pink-500 mr-1"></div>
                            <span>Dynamic: 12</span>
                          </div>
                          <div className="flex items-center">
                            <div className="h-2 w-2 rounded-full bg-green-500 mr-1"></div>
                            <span>High-Value: 4</span>
                          </div>
                          <div className="flex items-center">
                            <div className="h-2 w-2 rounded-full bg-gray-400 mr-1"></div>
                            <span>Inactive: 2</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    {/* Conversion Metrics */}
                    <Card className="bg-white/70 shadow-sm">
                      <CardHeader className="pb-2 pt-3">
                        <CardTitle className="text-sm font-medium">Conversion Metrics</CardTitle>
                      </CardHeader>
                      <CardContent className="pb-3">
                        <div className="space-y-2">
                          <div className="flex justify-between items-center text-xs">
                            <span>Avg. Conversion Rate</span>
                            <span className="font-medium">38%</span>
                          </div>
                          <div className="w-full h-1.5 bg-orange-200 rounded-full overflow-hidden">
                            <div className="bg-orange-500 h-1.5" style={{ width: '38%' }} />
                          </div>
                          <div className="flex justify-between items-center text-xs">
                            <span>Top Segment</span>
                            <span className="font-medium">62%</span>
                          </div>
                          <div className="w-full h-1.5 bg-pink-200 rounded-full overflow-hidden">
                            <div className="bg-pink-500 h-1.5" style={{ width: '62%' }} />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  {/* Live Traffic Chart */}
                  <Card className="bg-white/70 shadow-sm mb-3">
                    <CardHeader className="pb-1 pt-3">
                      <CardTitle className="text-sm font-medium">Live Traffic Chart</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 pb-2">
                      <div className="bg-gray-100 rounded-md overflow-hidden relative h-24 flex items-center justify-center">
                        <svg viewBox="0 0 100 30" className="w-full h-full stroke-orange-500 stroke-2 fill-orange-100/70 stroke-linecap-round stroke-linejoin-round">
                          <defs>
                            <linearGradient id="martech-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                              <stop offset="0%" stopColor="rgb(249, 115, 22)" stopOpacity="0.5" />
                              <stop offset="100%" stopColor="rgb(249, 115, 22)" stopOpacity="0" />
                            </linearGradient>
                          </defs>
                          <path d="M0,20 Q10,15 20,18 T40,10 T60,15 T80,12 T100,20 L100,30 H0 Z" fill="url(#martech-gradient)" />
                          <path d="M0,20 Q10,15 20,18 T40,10 T60,15 T80,12 T100,20" fill="none" />
                        </svg>
                        <div className="absolute bottom-1 right-1 text-xs bg-white/80 rounded px-1">Last 24h</div>
                      </div>
                    </CardContent>
                  </Card>
                  {/* Automation Score */}
                  <Card className="bg-white/70 shadow-sm">
                    <CardHeader className="pb-1 pt-3">
                      <div className="flex justify-between items-center">
                        <CardTitle className="text-sm font-medium">Automation Score</CardTitle>
                        <Badge variant="outline" className="text-xs h-5 px-1.5 bg-orange-100 text-orange-800 hover:bg-orange-200 border-orange-200">
                          92/100
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0 pb-2">
                      <div className="space-y-1.5">
                        <div className="text-xs p-1.5 rounded bg-gray-100/80 flex items-center justify-between">
                          <span className="font-medium">Workflows Automated</span>
                          <Badge variant="outline" className="text-xs h-5 px-1.5 border-orange-200 text-orange-800 bg-orange-50">17</Badge>
                        </div>
                        <div className="text-xs p-1.5 rounded bg-gray-100/80 flex items-center justify-between">
                          <span className="font-medium">Manual Tasks</span>
                          <Badge variant="outline" className="text-xs h-5 px-1.5 border-pink-200 text-pink-800 bg-pink-50">3</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
                {/* Campaigns Tab */}
                <TabsContent value="campaigns" className="p-3">
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    {/* Top Campaigns */}
                    <Card className="bg-white/70 shadow-sm">
                      <CardHeader className="pb-2 pt-3">
                        <CardTitle className="text-sm font-medium">Top Campaigns</CardTitle>
                      </CardHeader>
                      <CardContent className="pb-3">
                        <div className="flex flex-col gap-2 text-xs">
                          <span>Holiday Sale: <span className="font-medium text-orange-600">5.2x ROI</span></span>
                          <span>Spring Launch: <span className="font-medium text-pink-600">4.1x ROI</span></span>
                          <span>Referral Drive: <span className="font-medium text-green-600">3.7x ROI</span></span>
                        </div>
                      </CardContent>
                    </Card>
                    {/* Content Performance */}
                    <Card className="bg-white/70 shadow-sm">
                      <CardHeader className="pb-2 pt-3">
                        <CardTitle className="text-sm font-medium">Content Performance</CardTitle>
                      </CardHeader>
                      <CardContent className="pb-3">
                        <div className="space-y-2">
                          <div className="flex justify-between items-center text-xs">
                            <span>Avg. Engagement</span>
                            <span className="font-medium">42%</span>
                          </div>
                          <div className="w-full h-1.5 bg-pink-200 rounded-full overflow-hidden">
                            <div className="bg-pink-500 h-1.5" style={{ width: '42%' }} />
                          </div>
                          <div className="flex justify-between items-center text-xs">
                            <span>Top Channel</span>
                            <span className="font-medium">Email</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  {/* Campaign Trends Chart */}
                  <Card className="bg-white/70 shadow-sm mb-3">
                    <CardHeader className="pb-1 pt-3">
                      <CardTitle className="text-sm font-medium">Campaign Trends</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 pb-3">
                      <div className="h-24 bg-gray-50 rounded-md overflow-hidden relative flex items-center justify-center">
                        <svg viewBox="0 0 100 30" className="w-full h-full stroke-pink-500 stroke-2 fill-pink-100/70 stroke-linecap-round stroke-linejoin-round">
                          <defs>
                            <linearGradient id="campaign-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                              <stop offset="0%" stopColor="rgb(236, 72, 153)" stopOpacity="0.5" />
                              <stop offset="100%" stopColor="rgb(236, 72, 153)" stopOpacity="0" />
                            </linearGradient>
                          </defs>
                          <path d="M0,25 Q10,20 20,22 T40,15 T60,18 T80,12 T100,20 L100,30 H0 Z" fill="url(#campaign-gradient)" />
                          <path d="M0,25 Q10,20 20,22 T40,15 T60,18 T80,12 T100,20" fill="none" />
                        </svg>
                        <div className="absolute bottom-1 right-1 text-xs bg-white/80 rounded px-1">Last 30 days</div>
                      </div>
                    </CardContent>
                  </Card>
                  {/* A/B Test Results */}
                  <Card className="bg-white/70 shadow-sm">
                    <CardHeader className="pb-1 pt-3">
                      <CardTitle className="text-sm font-medium">A/B Test Results</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 pb-2">
                      <div className="space-y-1.5">
                        <div className="text-xs p-1.5 rounded bg-gray-100/80 flex items-center justify-between">
                          <span className="font-medium">Variant A</span>
                          <Badge variant="outline" className="text-xs h-5 px-1.5 border-orange-200 text-orange-800 bg-orange-50">Winner</Badge>
                        </div>
                        <div className="text-xs p-1.5 rounded bg-gray-100/80 flex items-center justify-between">
                          <span className="font-medium">Variant B</span>
                          <Badge variant="outline" className="text-xs h-5 px-1.5 border-pink-200 text-pink-800 bg-pink-50">Runner-up</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
                {/* Alerts Tab */}
                <TabsContent value="alerts" className="p-3">
                  <div className="space-y-2">
                    <div className="bg-white border-l-4 border-orange-500 rounded shadow-sm p-3 flex items-start">
                      <BarChart3 className="h-4 w-4 text-orange-500 flex-shrink-0 mt-0.5" />
                      <div className="ml-2">
                        <h4 className="text-sm font-medium">Performance Alert</h4>
                        <p className="text-xs text-muted-foreground">Conversion rate dropped 8% on Spring Launch campaign. Review targeting and creative.</p>
                        <div className="mt-1 text-xs flex items-center">
                          <ArrowRight className="h-3 w-3 mr-1" />
                          <span className="text-muted-foreground">15 minutes ago</span>
                          <Button variant="link" size="sm" className="h-auto p-0 ml-2 text-xs">
                            View Details
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white border-l-4 border-pink-500 rounded shadow-sm p-3 flex items-start">
                      <BarChart3 className="h-4 w-4 text-pink-500 flex-shrink-0 mt-0.5" />
                      <div className="ml-2">
                        <h4 className="text-sm font-medium">Engagement Warning</h4>
                        <p className="text-xs text-muted-foreground">Engagement on social channels below average. Consider content refresh.</p>
                        <div className="mt-1 text-xs flex items-center">
                          <ArrowRight className="h-3 w-3 mr-1" />
                          <span className="text-muted-foreground">1 hour ago</span>
                          <Button variant="link" size="sm" className="h-auto p-0 ml-2 text-xs">
                            View Details
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white border-l-4 border-green-500 rounded shadow-sm p-3 flex items-start">
                      <BarChart3 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <div className="ml-2">
                        <h4 className="text-sm font-medium">Info</h4>
                        <p className="text-xs text-muted-foreground">New automation workflow available for campaign scheduling.</p>
                        <div className="mt-1 text-xs flex items-center">
                          <ArrowRight className="h-3 w-3 mr-1" />
                          <span className="text-muted-foreground">Today</span>
                          <Button variant="link" size="sm" className="h-auto p-0 ml-2 text-xs">
                            Review Workflow
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
            {/* Dashboard Footer */}
            <div className="bg-gray-100/80 p-2 flex justify-between items-center text-xs text-muted-foreground">
              <div>Powered by OpenLabs AI</div>
              <div className="flex items-center">
                <Button variant="ghost" size="sm" className="h-6 text-xs gap-1">
                  <Zap className="h-3 w-3" /> Optimize Campaigns
                </Button>
                <Button variant="ghost" size="sm" className="h-6 text-xs gap-1">
                  <ArrowRight className="h-3 w-3" /> Full Dashboard
                </Button>
              </div>
            </div>
          </div>
          {/* Summary Metrics Row */}
          <div className="mt-6 grid grid-cols-3 gap-4 text-center">
            <div className="bg-white/80 backdrop-blur-sm p-3 rounded-lg">
              <p className="text-2xl font-bold text-orange-600">38%</p>
              <p className="text-sm text-gray-600">Higher Conversion</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm p-3 rounded-lg">
              <p className="text-2xl font-bold text-orange-600">5.2x</p>
              <p className="text-sm text-gray-600">Campaign ROI</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm p-3 rounded-lg">
              <p className="text-2xl font-bold text-orange-600">42%</p>
              <p className="text-sm text-gray-600">Time Saved</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Main Benefits */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={fadeIn}
        className="text-center mb-16"
      >
        <h2 className="text-3xl font-bold mb-12">Why Choose Our MarTech Solutions?</h2>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="bg-orange-100 text-orange-700 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Target className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Precision Targeting</h3>
            <p className="text-gray-600">
              AI algorithms that continuously learn from campaign performance to refine audience targeting and messaging for maximum impact.
            </p>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="bg-orange-100 text-orange-700 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4">
              <BarChart3 className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Data-Driven Decisions</h3>
            <p className="text-gray-600">
              Transform marketing from intuition-based to data-driven with real-time analytics and actionable insights across all channels.
            </p>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="bg-orange-100 text-orange-700 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Zap className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Operational Efficiency</h3>
            <p className="text-gray-600">
              Automate repetitive marketing tasks and workflows, allowing your team to focus on strategy and creative initiatives.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Solutions */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
        className="mb-20"
      >
        <h2 className="text-3xl font-bold mb-12 text-center">Our MarTech Solutions</h2>
        
        <div className="grid gap-8">
          {solutions.map((solution, index) => (
            <motion.div 
              key={solution.title}
              variants={fadeIn}
              className={`flex flex-col md:flex-row gap-8 bg-white p-6 rounded-xl shadow-sm border border-gray-100 ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}
            >
              <div className="md:w-1/3 flex flex-col items-center md:items-start text-center md:text-left">
                <div className="bg-gradient-to-r from-orange-500 to-pink-600 text-white w-16 h-16 rounded-xl flex items-center justify-center mb-4">
                  <solution.icon className="h-8 w-8" />
                </div>
                <h3 className="text-2xl font-semibold mb-2">{solution.title}</h3>
                <p className="text-gray-600 mb-4">{solution.description}</p>
              </div>
              
              <div className="md:w-2/3 bg-gray-50 rounded-xl p-6">
                <h4 className="font-semibold mb-4">Key Benefits:</h4>
                <div className="grid sm:grid-cols-2 gap-3">
                  {solution.benefits.map((benefit) => (
                    <div key={benefit} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm">{benefit}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Applicable Use Cases */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={fadeIn}
        className="mb-20"
      >
        <h2 className="text-3xl font-bold mb-4 text-center">Applicable Use Cases</h2>
        <p className="text-center text-muted-foreground max-w-3xl mx-auto mb-12">
          Our MarTech solutions can be tailored for various industry applications, providing significant advantages across different marketing and customer engagement sectors.
        </p>
        <Tabs defaultValue={useCases[0].industry.toLowerCase().replace(/\s+/g, '-')} className="w-full">
          <TabsList className="w-full justify-start mb-8 overflow-auto">
            {useCases.map((useCase) => (
              <TabsTrigger 
                key={useCase.industry} 
                value={useCase.industry.toLowerCase().replace(/\s+/g, '-')}
                className="px-6"
              >
                {useCase.industry}
              </TabsTrigger>
            ))}
          </TabsList>
          {useCases.map((useCase) => (
            <TabsContent 
              key={useCase.industry}
              value={useCase.industry.toLowerCase().replace(/\s+/g, '-')}
              className="bg-white p-8 rounded-xl shadow-sm border border-gray-100"
            >
              <div className="flex flex-col md:flex-row gap-8">
                <div className="md:w-1/2">
                  <Badge className="mb-3 bg-orange-100 text-orange-700 hover:bg-orange-200">
                    {useCase.industry}
                  </Badge>
                  <h3 className="text-2xl font-semibold mb-4">{useCase.company}</h3>
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-semibold mb-1">Industry Challenge</h4>
                      <p className="text-gray-600">{useCase.challenge}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Key Applications</h4>
                      <ul className="space-y-2">
                        {useCase.industry === "E-commerce" && (
                          <>
                            <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" /><span>AI-powered customer segmentation and targeting</span></li>
                            <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" /><span>Dynamic product recommendations and personalization</span></li>
                            <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" /><span>Automated email and campaign orchestration</span></li>
                            <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" /><span>Real-time analytics and conversion optimization</span></li>
                          </>
                        )}
                        {useCase.industry === "Media & Entertainment" && (
                          <>
                            <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" /><span>Predictive analytics for content engagement and churn</span></li>
                            <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" /><span>AI content generation and A/B testing</span></li>
                            <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" /><span>Cross-channel campaign orchestration</span></li>
                            <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" /><span>Audience journey optimization</span></li>
                          </>
                        )}
                        {useCase.industry === "Financial Services" && (
                          <>
                            <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" /><span>Omnichannel campaign management with compliance automation</span></li>
                            <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" /><span>Lead scoring and predictive analytics for acquisition</span></li>
                            <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" /><span>Personalized content and offer generation</span></li>
                            <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" /><span>Regulatory-compliant marketing workflows</span></li>
                          </>
                        )}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Expected Benefits</h4>
                      <p className="text-gray-600">{useCase.results}</p>
                    </div>
                  </div>
                  <Button variant="link" className="mt-6 pl-0">
                    Learn More About {useCase.industry} Solutions <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
                <div className="md:w-1/2 bg-gray-50 rounded-xl p-8">
                  <div className="bg-white rounded-lg p-6 shadow-sm">
                    <h4 className="font-medium text-lg mb-4 text-center">Technology Stack</h4>
                    <div className="space-y-4">
                      {useCase.industry === "E-commerce" && (
                        <>
                          <div className="flex items-start gap-3">
                            <div className="bg-orange-100 p-2 rounded-md">
                              <Users className="h-5 w-5 text-orange-700" />
                            </div>
                            <div>
                              <h5 className="font-medium text-sm">Customer Segmentation AI</h5>
                              <p className="text-sm text-gray-600">Real-time micro-segmentation and targeting for higher conversion rates.</p>
                            </div>
                          </div>
                          <div className="flex items-start gap-3">
                            <div className="bg-orange-100 p-2 rounded-md">
                              <ShoppingCart className="h-5 w-5 text-orange-700" />
                            </div>
                            <div>
                              <h5 className="font-medium text-sm">Dynamic Recommendation Engine</h5>
                              <p className="text-sm text-gray-600">Personalized product and content recommendations with adaptive learning.</p>
                            </div>
                          </div>
                        </>
                      )}
                      {useCase.industry === "Media & Entertainment" && (
                        <>
                          <div className="flex items-start gap-3">
                            <div className="bg-orange-100 p-2 rounded-md">
                              <LineChart className="h-5 w-5 text-orange-700" />
                            </div>
                            <div>
                              <h5 className="font-medium text-sm">Predictive Analytics Suite</h5>
                              <p className="text-sm text-gray-600">Forecast engagement, churn, and campaign performance with 85%+ accuracy.</p>
                            </div>
                          </div>
                          <div className="flex items-start gap-3">
                            <div className="bg-orange-100 p-2 rounded-md">
                              <MessageSquare className="h-5 w-5 text-orange-700" />
                            </div>
                            <div>
                              <h5 className="font-medium text-sm">AI Content Generation</h5>
                              <p className="text-sm text-gray-600">Automated content creation and A/B testing for optimal engagement.</p>
                            </div>
                          </div>
                        </>
                      )}
                      {useCase.industry === "Financial Services" && (
                        <>
                          <div className="flex items-start gap-3">
                            <div className="bg-orange-100 p-2 rounded-md">
                              <Megaphone className="h-5 w-5 text-orange-700" />
                            </div>
                            <div>
                              <h5 className="font-medium text-sm">Omnichannel Orchestration</h5>
                              <p className="text-sm text-gray-600">Centralized campaign management with compliance and approval workflows.</p>
                            </div>
                          </div>
                          <div className="flex items-start gap-3">
                            <div className="bg-orange-100 p-2 rounded-md">
                              <BarChart3 className="h-5 w-5 text-orange-700" />
                            </div>
                            <div>
                              <h5 className="font-medium text-sm">Predictive Lead Scoring</h5>
                              <p className="text-sm text-gray-600">AI-driven lead qualification and acquisition optimization.</p>
                            </div>
                          </div>
                        </>
                      )}
                      <div className="pt-4 mt-4 border-t border-gray-100 text-center">
                        <Badge className="bg-orange-50 text-orange-800 hover:bg-orange-100">
                          Compatible with existing marketing technology stacks
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </motion.section>

      {/* Technology Stack */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={fadeIn}
        className="mb-20"
      >
        <h2 className="text-3xl font-bold mb-6 text-center">Technology Stack</h2>
        <p className="text-center text-gray-600 max-w-3xl mx-auto mb-12">
          Our MarTech solutions are built on a cutting-edge technology stack that combines powerful AI capabilities with scalable cloud infrastructure.
        </p>
        
        <div className="grid md:grid-cols-3 gap-6">
          {technologies.map((tech) => (
            <Card key={tech.name} className="bg-white">
              <CardHeader>
                <CardTitle>{tech.name}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-600">
                  {tech.description}
                </CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={fadeIn}
        className="bg-gradient-to-r from-orange-500 to-pink-600 text-white rounded-2xl p-12 text-center"
      >
        <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Marketing?</h2>
        <p className="max-w-2xl mx-auto mb-8">
          Schedule a personalized demo to see how our MarTech solutions can drive growth for your business. Our team of experts will analyze your current marketing stack and recommend the best solutions for your goals.
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <Button size="lg" variant="default" className="bg-white text-orange-600 hover:bg-gray-100">
            Request Demo
          </Button>
          <Button size="lg" variant="outline" className="text-white border-white hover:bg-white/10">
            Talk to Sales
          </Button>
        </div>
      </motion.section>

      {/* FAQ Section */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={fadeIn}
        className="mb-20"
      >
        <h2 className="text-3xl font-bold mb-12 text-center">Frequently Asked Questions</h2>
        
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>How quickly can I implement these solutions?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                Most of our MarTech solutions can be integrated within 2-4 weeks, with initial results visible within the first month. Our no-code options allow for even faster implementation, sometimes in as little as 72 hours.
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Do I need to replace my existing marketing stack?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                No. Our solutions are designed to integrate seamlessly with your existing marketing technology stack. We support all major platforms including Salesforce, HubSpot, Adobe Experience Cloud, and more.
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>How do you ensure data privacy compliance?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                All our solutions are built with privacy by design principles and comply with GDPR, CCPA, and other global privacy regulations. We offer data residency options and implement advanced data protection measures.
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>What kind of ROI can I expect?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                Our clients typically see ROI between 3-7x their investment within the first year. Specific results vary by industry and use case, but we provide detailed performance tracking dashboards to measure impact.
              </p>
            </CardContent>
          </Card>
        </div>
      </motion.section>
    </div>
  );
} 